/**
 * 환율 위젯 (#406) — 매매기준율/살때/팔때 + 금액 자동 환산 계산기.
 * 다음 금융(무키) 서버 수집 → Firestore onSnapshot. USD/KRW, PHP/KRW 직접, USD/PHP 는 교차.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";

interface Pair {
  base?: number;
  cash_buy?: number;
  cash_sell?: number;
  change?: number;
}
interface ExchangeData {
  usd_krw?: Pair;
  php_krw?: Pair;
  updated_at?: string;
}

type Cur = "USD" | "PHP" | "KRW";

export default function ExchangeWidget() {
  const { t } = useTranslation();
  const [data, setData] = useState<ExchangeData | null>(null);
  const [amount, setAmount] = useState("1");
  const [from, setFrom] = useState<Cur>("USD");
  const [to, setTo] = useState<Cur>("KRW");

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "trippang_widgets", "exchange"),
      (snap) => { if (snap.exists()) setData(snap.data() as ExchangeData); },
      (err) => console.error("exchange widget snapshot:", err)
    );
    return () => unsub();
  }, []);

  // 매매기준율 기준 KRW 환산표 (1 단위 통화 = ? KRW)
  const krwPer = useMemo<Record<Cur, number>>(() => ({
    USD: data?.usd_krw?.base || 0,
    PHP: data?.php_krw?.base || 0,
    KRW: 1,
  }), [data]);

  const converted = useMemo(() => {
    const amt = parseFloat(amount);
    if (!amt || !krwPer[from] || !krwPer[to]) return null;
    return (amt * krwPer[from]) / krwPer[to];
  }, [amount, from, to, krwPer]);

  const fmt = (n?: number) =>
    n == null ? "-" : n.toLocaleString(undefined, { maximumFractionDigits: 2 });

  const curs: Cur[] = ["USD", "PHP", "KRW"];

  return (
    <div className="border-b border-slate-200 px-3 py-2.5">
      <div className="mb-1.5 text-xs font-semibold text-sky-600">{t("widget.exchangeTitle")}</div>
      {!data ? (
        <div className="py-1 text-[11px] text-slate-400">{t("widget.loading")}</div>
      ) : (
        <>
          {/* 범례 + 데이터 행을 하나의 grid로 묶어 컬럼 폭 공유 (분리된 grid/행마다 쌓으면
              라벨 길이·자릿수 차이로 세로 정렬이 안 맞음) */}
          <div className="grid grid-cols-[auto_1fr_1fr_1fr] items-center gap-x-2 gap-y-0.5">
            <span />
            <span className="text-right text-[9px] text-slate-500">{t("widget.fxBase")}</span>
            <span className="text-right text-[9px] text-red-500">{t("widget.fxBuy")}</span>
            <span className="text-right text-[9px] text-blue-500">{t("widget.fxSell")}</span>

            <span className="text-[11px] text-slate-600">USD/KRW</span>
            <span className="text-right text-[11px] tabular-nums font-semibold text-slate-900">{fmt(data.usd_krw?.base)}</span>
            <span className="text-right text-[11px] tabular-nums text-red-500">{fmt(data.usd_krw?.cash_buy)}</span>
            <span className="text-right text-[11px] tabular-nums text-blue-500">{fmt(data.usd_krw?.cash_sell)}</span>

            <span className="text-[11px] text-slate-600">PHP/KRW</span>
            <span className="text-right text-[11px] tabular-nums font-semibold text-slate-900">{fmt(data.php_krw?.base)}</span>
            <span className="text-right text-[11px] tabular-nums text-red-500">{fmt(data.php_krw?.cash_buy)}</span>
            <span className="text-right text-[11px] tabular-nums text-blue-500">{fmt(data.php_krw?.cash_sell)}</span>
          </div>

          {/* 계산기 */}
          <div className="mt-2 border-t border-slate-100 pt-2">
            <div className="flex items-center gap-1">
              <input
                type="number"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full min-w-0 rounded border border-slate-300 px-1.5 py-0.5 text-[11px] text-slate-900 outline-none focus:border-sky-400"
              />
              <select value={from} onChange={(e) => setFrom(e.target.value as Cur)}
                className="rounded border border-slate-300 px-0.5 py-0.5 text-[11px] text-slate-700 outline-none">
                {curs.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="mt-1 flex items-center gap-1">
              <div className="w-full min-w-0 truncate rounded bg-slate-50 px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-slate-900">
                {fmt(converted ?? undefined)}
              </div>
              <select value={to} onChange={(e) => setTo(e.target.value as Cur)}
                className="rounded border border-slate-300 px-0.5 py-0.5 text-[11px] text-slate-700 outline-none">
                {curs.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
